import { useTranslation } from 'react-i18next';
import * as api from '../api';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { LoginWithoutAccountForm } from '../components/LoginWithoutAccountForm';
import { BalanceContext } from '../context/BalanceContext';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';

export const BalancesQueryDialog = (props: {
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { setBalances } = useContext(BalanceContext);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();
  const [error, setError] = useState<string>();

  const handlePayClick = async (vals: {
    lastName: string;
    queryValue: string;
    queryType: 'orderNumber' | 'passportNoOrCitizenshipNumber';
  }) => {
    try {
      setSubmitting(true);
      const params =
        vals.queryType === 'orderNumber'
          ? { lastName: vals.lastName, orderNumber: vals.queryValue }
          : {
              lastName: vals.lastName,
              passportNoOrCitizenshipNumber: vals.queryValue,
            };
      const res = await api.balances(params);
      const updatedBalances = await Promise.all(
        res.map(async (item) => {
          //initial toPay fill
          let toPay = {
            amount: item.payment.balance.amount,
            currency: { id: 724, code: 'TRY', name: 'Turkish Lira' },
            currencyExchangeRate: 0,
          } as api.ApiAmountWithCurrency;

          if (toPay.amount > 0)
            toPay = await api.currencyExchange(
              item.payment.balance.currency.code,
              'TRY',
              item.payment.balance.amount,
            );

          return {
            ...item,
            payment: {
              ...item.payment,
              loyalty: {
                points: 0,
                fiat: 0,
              },
              received: {
                amount:
                  (item.payment.total.amount || 0) -
                  (item.payment.balance.amount || 0),
                currency: item.payment.balance.currency,
              },
              toPay: {
                amount: toPay?.amount || 0,
                currencyExchangeRate:
                  item.payment.balance.amount > 0
                    ? item.payment.balance.amount / toPay?.amount
                    : 0, // 1 TRY => ?€ | ?$...
                // todo implement company currency
                currency: toPay.currency || null,
              },
            },
          };
        }),
      );

      setBalances(updatedBalances);
      nav('/balances');
      setSubmitting(false);
    } catch (e: any) {
      setError(
        e.detail || e.message || t('balancesQueryPage.balancesNotFound'),
      );
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <DialogContent
        sx={{ minWidth: isMobile ? '200px' : '500px', maxWidth: '700px' }}
      >
        {submitting && (
          <LoadingSpinner loadingText={t('balancesQueryPage.loading')} />
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: '700px',
            minWidth: isMobile ? '200px' : '500px',
          }}
        >
          <Box
            my={2}
            sx={{ fontSize: '24px', fontWeight: 900, textAlign: 'center' }}
          >
            {t('balancesQueryDialog.title')}
          </Box>
          <LoginWithoutAccountForm
            initialValues={{
              lastName: '',
              orderNumber: '',
              passportNoOrCitizenshipNumber: '',
            }}
            onSubmit={handlePayClick}
            hiddenLoginButton
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="inherit"
          onClick={props.onClose}
          sx={{ mr: 2 }}
        >
          {t('balancesQueryDialog.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
